/**
 * Disparo por ticket (spec 014): a regra da mesa que manda o modelo HSM para o
 * associado de cada ticket que chega no estágio de entrada, e a resposta dele
 * que move o ticket.
 *
 * As regras PURAS abaixo são portadas do worker que esta regra substitui
 * (pooltrack/src/hsm-tiflux-blip/outbox.ts), com os testes dele. O que era
 * constante lá (mesa 35886, estágio 214157, terça a quinta, 80/dia, 9h–17h,
 * quatro clientes bloqueados) virou campo da regra — a mesma lógica, parâmetros
 * por mesa. Nada aqui toca banco ou rede: roda na API e no front.
 */
import { z } from 'zod';
import { ChatChannelSchema } from './chats';
import { Dateish, IdCoerce, PaginationQuerySchema } from './common';
import { capitalize, type Terms } from './profiles';
import { parseSgrDescription } from './sgr';
import {
  EMPTY_TICKET_SHEET,
  resolveSheetSlot,
  ticketSheetFormLayer,
  ticketSheetPhones,
  type SheetPhone,
  type TicketSheetSlots,
  type TicketSheetSourceInput,
} from './ticket-sheet';
import type { CreatedVia } from './tickets';

// ── Constantes ──────────────────────────────────────────────────────────────

/** Fuso de TODA decisão de calendário do disparo (dia permitido, janela, cota "de hoje"). */
export const DISPATCH_TIMEZONE = 'America/Sao_Paulo';

/** Cota diária padrão — a do worker (DAILY_HSM_CAP). */
export const DISPATCH_DEFAULT_DAILY_CAP = 80;
/** Teto da cota que a regra aceita: acima disso já não é "disparo", é campanha. */
export const DISPATCH_MAX_DAILY_CAP = 1000;

/** Tamanho máximo do corpo de modelo informado à mão (canal da Blip, spec 025). */
export const BLIP_TEMPLATE_BODY_MAX = 1024;

/** Dias padrão (Date#getDay, Dom=0): terça, quarta e quinta — os do worker. */
export const DISPATCH_DEFAULT_WEEKDAYS: readonly number[] = [2, 3, 4];

/** Janela padrão, [início, fim) em BRT — o isBusinessHour do worker (9h–17h). */
export const DISPATCH_DEFAULT_WINDOW = { start: '09:00', end: '17:00' } as const;

// ── Calendário ──────────────────────────────────────────────────────────────

const WEEKDAY_BY_NAME: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

/**
 * Dia da semana (Dom=0 … Sáb=6) de `now` NO FUSO DE BRASÍLIA. Usar o UTC do
 * servidor erra toda noite depois das 21h: meia-noite UTC de terça ainda é
 * segunda em Brasília, e o disparo sairia no dia errado.
 */
export function getDayOfWeekInBRT(now: Date = new Date()): number {
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: DISPATCH_TIMEZONE,
    weekday: 'long',
  })
    .formatToParts(now)
    .find((p) => p.type === 'weekday')?.value;
  return name !== undefined ? (WEEKDAY_BY_NAME[name] ?? -1) : -1;
}

/** Minutos desde a meia-noite, em Brasília (0..1439). */
export function minutesOfDayInBRT(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: DISPATCH_TIMEZONE,
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return (hour % 24) * 60 + minute;
}

/** Data civil em Brasília, `YYYY-MM-DD` — a "hoje" da cota. */
export function dateKeyInBRT(now: Date = new Date()): string {
  // en-CA formata como YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPATCH_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function isAllowedDispatchDay(weekdays: readonly number[], now: Date = new Date()): boolean {
  return weekdays.includes(getDayOfWeekInBRT(now));
}

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** `HH:MM` → minutos; null quando fora do formato. */
export function parseHHMM(value: string): number | null {
  const m = HHMM_RE.exec(value);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/**
 * Dentro da janela [início, fim) em Brasília. Fim EXCLUSIVO, como o worker
 * (`hour < 17`): às 17:00 em ponto já não sai. Janela malformada ou vazia é
 * "fora" — falha fechada, nunca "o dia inteiro".
 */
export function isWithinWindow(
  windowStart: string,
  windowEnd: string,
  now: Date = new Date()
): boolean {
  const start = parseHHMM(windowStart);
  const end = parseHHMM(windowEnd);
  if (start === null || end === null || start >= end) return false;
  const minute = minutesOfDayInBRT(now);
  return minute >= start && minute < end;
}

// ── Cota e decisão do ciclo ─────────────────────────────────────────────────

/**
 * Quantos disparos ainda cabem hoje. Nunca negativo — "estouramos N" não é
 * permissão para nada. Contagem corrompida (negativa, NaN) devolve a cota
 * cheia, como no worker.
 */
export function computeRemainingQuota(
  attemptsToday: number,
  cap: number = DISPATCH_DEFAULT_DAILY_CAP
): number {
  if (!Number.isFinite(attemptsToday) || attemptsToday < 0) return cap;
  return Math.max(0, cap - Math.floor(attemptsToday));
}

export type DispatchSkipReason = 'outside_window' | 'wrong_day' | 'cap_reached';

export type DispatchDecision =
  { action: 'dispatch'; remaining: number } | { action: 'skip'; reason: DispatchSkipReason };

export interface DispatchDecisionInputs {
  isWithinWindow: boolean;
  isAllowedDay: boolean;
  attemptsToday: number;
  cap?: number;
}

/**
 * Os três portões do ciclo numa decisão. A ordem é a do worker e é
 * intencional: janela primeiro (mais barata e mais ampla), dia depois, cota
 * por último (exige leitura do banco) — o motivo registrado é estável.
 */
export function decideDispatch(inputs: DispatchDecisionInputs): DispatchDecision {
  if (!inputs.isWithinWindow) return { action: 'skip', reason: 'outside_window' };
  if (!inputs.isAllowedDay) return { action: 'skip', reason: 'wrong_day' };
  const remaining = computeRemainingQuota(inputs.attemptsToday, inputs.cap);
  if (remaining <= 0) return { action: 'skip', reason: 'cap_reached' };
  return { action: 'dispatch', remaining };
}

// ── Reconferência do ticket no instante do envio ────────────────────────────

export interface DispatchTicketState {
  isClosed: boolean;
  stageId: number | null;
  deskId: number | null;
}

export type DispatchIneligibleReason = 'closed' | 'wrong_stage' | 'wrong_desk' | 'unknown_ticket';

export type DispatchEligibility =
  { eligible: true } | { eligible: false; reason: DispatchIneligibleReason };

/**
 * O ticket ainda pode receber o disparo? A fila tem cota diária e dias
 * permitidos, então uma linha enfileirada hoje pode esperar semanas pela vez;
 * no worker, em set/2026, 95% da fila acumulada já não era elegível — fechado,
 * agendado, ou com atendente conversando. O estado de quando entrou na fila
 * nunca vale: relê-se o ticket logo antes do envio e decide-se aqui.
 *
 * Diferença do worker: lá `deskId` nulo "não bloqueava" (payload v1 do TiFlux
 * às vezes omitia a mesa); aqui o ticket vem do banco e sempre tem mesa, então
 * mesa nula é tratada como mesa errada — nunca como licença para enviar.
 */
export function checkDispatchEligibility(
  ticket: DispatchTicketState | null | undefined,
  rule: { deskId: number; entryStageId: number }
): DispatchEligibility {
  if (!ticket) return { eligible: false, reason: 'unknown_ticket' };
  if (ticket.isClosed) return { eligible: false, reason: 'closed' };
  if (ticket.deskId !== rule.deskId) return { eligible: false, reason: 'wrong_desk' };
  if (ticket.stageId !== rule.entryStageId) return { eligible: false, reason: 'wrong_stage' };
  return { eligible: true };
}

// ── Telefone ────────────────────────────────────────────────────────────────

/**
 * Números de mentira que o SGR manda quando não sabe o telefone. Na forma de
 * fixo de 10 dígitos; celular perde o 9 antes da comparação, então
 * 31 9 0000-0000 ainda casa com 3100000000.
 *
 * `4733333333` não vem do SGR: é o telefone do solicitante FIXO que o worker da
 * PoolTrack grava em todo ticket que cria no TiFlux (+55 47 3333-3333, F-79).
 * Passa no formato enviável e é o número de um terceiro, igual em milhares de
 * tickets — se aparecer numa ficha, não é telefone do associado.
 */
export const KNOWN_STUB_PHONES: readonly string[] = [
  '0000000000',
  '3100000000',
  '3533333333',
  '9999999999',
  '8333333333',
  '3133333333',
  '3333333333',
  '35333333333',
  '4733333333',
];

export const normalizePhoneDigits = (raw: string | null | undefined): string =>
  String(raw ?? '').replace(/[^0-9]/g, '');

const stripMobile9 = (n: string): string =>
  n.length === 11 && n[2] === '9' ? n.slice(0, 2) + n.slice(3) : n;

/**
 * true quando `national` (número nacional, sem DDI) é um número de ENCHIMENTO
 * conhecido: um da lista acima, com ou sem o 9, ou um dígito só repetido.
 *
 * Diferente do isStubPhone, NÃO julga o formato: um número estrangeiro não é
 * enchimento — só não é enviável. É a pergunta da API do bot, que aceita
 * estrangeiro mas recusa enchimento (achado 4 da revisão da spec 025): o
 * solicitante de todo ticket do worker é o mesmo número de um terceiro.
 */
export function isFillerPhone(national: string | null | undefined): boolean {
  const n = normalizePhoneDigits(national);
  if (!n) return false;
  if (/^(\d)\1+$/.test(n)) return true;
  return KNOWN_STUB_PHONES.includes(n) || KNOWN_STUB_PHONES.includes(stripMobile9(n));
}

/**
 * true quando `raw` NÃO é um telefone de verdade (número nacional, sem DDI):
 * vazio, enchimento (isFillerPhone) ou fora do formato enviável.
 *
 * O dígito só repetido entra no enchimento porque o "0000000-0000" do SGR
 * normaliza para 11 zeros, que a entrada de 10 zeros da lista nunca casou: 69
 * tickets foram disparados para 5500000000000, cada um gastou uma vaga da cota
 * e voltou da Meta como "não entregue", com o ticket lendo "cliente contatado".
 */
export function isStubPhone(raw: string | null | undefined): boolean {
  const n = normalizePhoneDigits(raw);
  if (!n) return true;
  if (isFillerPhone(n)) return true;
  // número enviável no Brasil: DDD (2 dígitos, sem começar em 0) + 8 ou 9 dígitos
  return !/^[1-9][0-9]{9,10}$/.test(n);
}

/**
 * Número nacional (sem DDI) a partir de qualquer grafia: `+55 (31) 98752-4881`,
 * `5531987524881`, `31 98752-4881`. O DDI 55 só é retirado quando o que sobra
 * tem tamanho de número nacional (10–11) — um celular de DDD 55 (RS) tem 11
 * dígitos e começa com 55 sem ter DDI nenhum.
 */
export function toNationalPhone(raw: string | null | undefined): string {
  const digits = normalizePhoneDigits(raw);
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55'))
    return digits.slice(2);
  return digits;
}

/**
 * Um número digitado, em qualquer grafia, no formato do WhatsApp (E.164 sem
 * `+`: 55 + DDD + número) — ou null quando não presta (vazio, enchimento, fora
 * do formato). A MESMA normalização e a MESMA recusa para todo telefone que o
 * disparo considera, venha da ficha ou do solicitante.
 *
 * E para o número que uma pessoa digita na "Nova conversa" (20/10): ali ele ia
 * como foi digitado, e "31 98752-4881" saía para +31 (Holanda). Sete cobranças
 * da PoolTrack nunca chegaram, e o ERP mostrava "Enviada".
 */
export function sendablePhone(raw: string | null | undefined): string | null {
  const national = toNationalPhone(raw);
  return isStubPhone(national) ? null : `55${national}`;
}

/**
 * Telefone da FICHA do SGR na descrição, no formato do WhatsApp, ou null
 * quando ela não tem um que preste.
 *
 * Na ordem do worker: `TELEFONE:`, depois `TELEFONE ADICIONAL:` — a
 * alternativa passa pelo MESMO filtro (com os dois zerados, a adicional
 * escapava e o disparo ia para 5500000000000).
 *
 * Só a descrição, de propósito: quem decide o telefone do DISPARO é
 * resolveDispatchPhone, que sabe de onde veio o ticket. Não há parâmetro para o
 * solicitante (F-79, revisão da spec 025, D2): todo ticket que o worker da
 * PoolTrack cria no TiFlux tem o mesmo solicitante fixo, com um telefone de
 * placeholder de um terceiro.
 */
export function pickDispatchPhone(description: string | null | undefined): string | null {
  for (const candidate of parseSgrDescription(description).phones) {
    const phone = sendablePhone(candidate);
    if (phone !== null) return phone;
  }
  return null;
}

/**
 * De onde o disparo tira o telefone — e o que fica no evento do envio. `sheet`
 * (spec 031) é o telefone que uma PESSOA escreveu na ficha do ticket; a linha
 * `TELEFONE:` da descrição continua sendo `description`.
 */
export const DISPATCH_PHONE_SOURCES = ['sheet', 'description', 'requestor'] as const;
export const DispatchPhoneSourceSchema = z.enum(DISPATCH_PHONE_SOURCES);
export type DispatchPhoneSource = z.infer<typeof DispatchPhoneSourceSchema>;

/**
 * As origens de ticket em que o telefone do SOLICITANTE vale para o disparo:
 * só `web`, o ticket aberto à mão por alguém da equipe na tela do ERP (a rota
 * POST /tickets, que é a única a gravar `web`). Decisão do operador em 03/10,
 * pelo #7520: a atendente digitou o celular em "Telefone do solicitante",
 * deixou a descrição vazia, e o disparo falhou sem telefone.
 *
 * Fora, de propósito: `import` (o espelho do TiFlux — o solicitante de todo
 * ticket do worker é o placeholder de um terceiro, F-79), `portal`, `email` e
 * `form` (quem preencheu o solicitante foi gente de fora, não a equipe),
 * `scheduled` (o sistema, sem solicitante) e `api`.
 */
export const DISPATCH_REQUESTOR_PHONE_ORIGINS: readonly CreatedVia[] = ['web'];

/**
 * ONDE o disparo procura o telefone deste ticket, NA ORDEM em que procura. A
 * descrição sempre vem primeiro e sempre vence; o solicitante só entra no
 * ticket aberto à mão no ERP. É a mesma lista que a tela usa para dizer onde
 * procurou quando não achou — a explicação não pode divergir da decisão.
 */
export function dispatchPhoneSources(
  createdVia: string | null | undefined
): readonly DispatchPhoneSource[] {
  // a ficha (o que a pessoa escreveu, spec 031) vem antes da linha da
  // descrição em todo ticket; o formulário, só no aberto à mão
  return ticketSheetFormLayer(createdVia)
    ? ['sheet', 'description', 'requestor']
    : ['sheet', 'description'];
}

export interface DispatchPhoneInput {
  description: string | null | undefined;
  /** o "Telefone do solicitante" do ticket, como foi digitado */
  requestorPhone: string | null | undefined;
  /** `tickets.created_via` — decide se o solicitante entra (dispatchPhoneSources) */
  createdVia: string | null | undefined;
  /**
   * os telefones da FICHA que uma pessoa escreveu (spec 031) — OBRIGATÓRIO: um
   * consumidor que o esquecesse discordaria do envio calado. `EMPTY_TICKET_SHEET`
   * para quem não tem ficha.
   */
  sheet: Pick<TicketSheetSlots, 'phone' | 'phoneAlt'>;
}

/** a entrada do disparo vira a da ficha: só os telefones importam aqui */
function phoneSheetInput(input: DispatchPhoneInput): TicketSheetSourceInput {
  return {
    title: null,
    description: input.description ?? null,
    createdVia: input.createdVia,
    requestorName: null,
    requestorPhone: input.requestorPhone ?? null,
    equipmentName: null,
    sgrVehicleId: null,
    sheet: { ...EMPTY_TICKET_SHEET, phone: input.sheet.phone, phoneAlt: input.sheet.phoneAlt },
  };
}

const PHONE_SOURCE_OF: Record<SheetPhone['source'], DispatchPhoneSource> = {
  person: 'sheet',
  description: 'description',
  requestor: 'requestor',
};

export interface ResolvedDispatchPhone {
  /** E.164 sem `+` */
  phone: string;
  source: DispatchPhoneSource;
}

/**
 * O telefone do DISPARO e de onde ele veio, ou null quando nenhum lugar onde o
 * disparo procura tem um que preste — o ticket vai para "Dados inválidos" com
 * `phone_not_found`, como no worker.
 *
 * É uma CAMADA da ficha do ticket (spec 031, ticketSheetPhones), na ordem:
 *   1. o telefone e o adicional da ficha — o que uma pessoa escreveu vence a
 *      linha da mesma chave da descrição, inclusive apagado (`''`). Sem nenhum
 *      dos dois editado, é a lista de sempre da descrição (`Telefone:`,
 *      `Telefone adicional:`, na ordem em que aparecem);
 *   2. o telefone do solicitante — SÓ no ticket aberto à mão no ERP
 *      (`created_via = 'web'`, DISPATCH_REQUESTOR_PHONE_ORIGINS), com a mesma
 *      normalização e a mesma recusa de enchimento da ficha.
 * O primeiro que preste (sendablePhone) vence.
 *
 * Em todo o resto (importado do TiFlux, portal, e-mail…), a ficha e a
 * descrição só: o solicitante dos tickets do worker é um placeholder (F-79).
 *
 * É a ÚNICA decisão do telefone do disparo: o envio (sendClaimedDispatch), o
 * aviso "na fila sem telefone" da tela e a trava do ticket aberto à mão (D5)
 * chamam esta função.
 */
export function resolveDispatchPhone(input: DispatchPhoneInput): ResolvedDispatchPhone | null {
  for (const candidate of ticketSheetPhones(phoneSheetInput(input))) {
    const phone = sendablePhone(candidate.value);
    if (phone !== null) return { phone, source: PHONE_SOURCE_OF[candidate.source] };
  }
  return null;
}

/** "telefone do solicitante" — a origem do número, como o histórico a diz. */
export const DISPATCH_PHONE_SOURCE_LABELS: Record<DispatchPhoneSource, string> = {
  sheet: 'telefone da ficha',
  description: 'telefone da descrição',
  requestor: 'telefone do solicitante',
};

/**
 * O motivo `phone_not_found` dito com ONDE o disparo procurou (o código do
 * motivo é o mesmo para todos — é discriminador em painel e lista):
 * - ticket aberto à mão no ERP: descrição e solicitante;
 * - os outros: só a linha `Telefone:` da descrição.
 */
export function phoneNotFoundLabel(sources: readonly DispatchPhoneSource[]): string {
  // a tentativa gravada a partir da spec 031 procurou também na ficha
  if (sources.includes('sheet')) {
    return sources.includes('requestor')
      ? 'Sem telefone válido na ficha, na descrição nem no solicitante'
      : 'Sem telefone válido na ficha nem na descrição';
  }
  return sources.includes('requestor')
    ? 'Sem telefone válido na descrição nem no solicitante'
    : 'Sem linha “Telefone:” válida na descrição';
}

// ── A trava do ticket aberto à mão (spec 031, D5) ───────────────────────────

/**
 * Os códigos do 422 quando a mesa tem regra de disparo ligada e o ticket aberto
 * à mão fica sem o que o disparo usa: o telefone sempre; o nome e a placa
 * quando o modelo da regra os usa (respostas do operador, 05/10). O juiz é o
 * do envio — resolveDispatchPhone e resolveDispatchParams —, nunca uma cópia.
 * A resposta leva `details.deskName`.
 */
export const DISPATCH_SHEET_ERROR_CODES = [
  'dispatch_phone_required',
  'dispatch_name_required',
  'dispatch_plate_required',
] as const;
export type DispatchSheetErrorCode = (typeof DISPATCH_SHEET_ERROR_CODES)[number];

export const isDispatchSheetErrorCode = (code: unknown): code is DispatchSheetErrorCode =>
  typeof code === 'string' && (DISPATCH_SHEET_ERROR_CODES as readonly string[]).includes(code);

/**
 * A mensagem do 422, no vocabulário do tenant (§5), dizendo EXATAMENTE o que
 * preencher: "A mesa Instalar manda WhatsApp para o associado: preencha o
 * telefone do associado na ficha, com DDD." Sem o nome da mesa, "Esta mesa".
 */
export function dispatchSheetErrorMessage(
  code: DispatchSheetErrorCode,
  deskName: string | null | undefined,
  terms: Pick<Terms, 'contact' | 'resourceId'>
): string {
  const desk = deskName?.trim() ? `A mesa ${deskName.trim()}` : 'Esta mesa';
  switch (code) {
    case 'dispatch_phone_required':
      return `${desk} manda WhatsApp para o ${terms.contact}: preencha o telefone do ${terms.contact} na ficha, com DDD.`;
    case 'dispatch_name_required':
      return `${desk} manda WhatsApp para o ${terms.contact} pelo nome: preencha o nome do ${terms.contact} na ficha.`;
    case 'dispatch_plate_required':
      return `${desk} manda WhatsApp para o ${terms.contact} com “${capitalize(terms.resourceId)}”: preencha “${capitalize(terms.resourceId)}” na ficha.`;
  }
}

/**
 * O que a regra de disparo da mesa exige do ticket ABERTO À MÃO (D5 e as
 * respostas do operador): o telefone sempre; o nome e a placa quando o modelo
 * os usa. null = mesa sem regra, regra desligada, ou "exigir a ficha ao abrir
 * à mão" desligado na regra (o interruptor do lado rápido).
 */
export const DispatchSheetRequirementSchema = z.object({
  phone: z.literal(true),
  name: z.boolean(),
  plate: z.boolean(),
});
export type DispatchSheetRequirement = z.infer<typeof DispatchSheetRequirementSchema>;

export function dispatchSheetRequirement(
  rule: { enabled: boolean; requireSheetOnManual: boolean; params: readonly Pick<DispatchParam, 'source'>[] } | null
): DispatchSheetRequirement | null {
  if (!rule || !rule.enabled || !rule.requireSheetOnManual) return null;
  return {
    phone: true,
    name: rule.params.some((p) => p.source === 'first_name'),
    plate: rule.params.some((p) => p.source === 'plate'),
  };
}

/** O ticket como a trava o julga: o que o disparo lê para telefone, nome e placa. */
export type DispatchSheetTicket = DispatchParamTicket & {
  requestorPhone: string | null | undefined;
  sheet: Pick<TicketSheetSlots, 'name' | 'plate' | 'phone' | 'phoneAlt'>;
};

/**
 * A trava (D5): o que falta para o disparo, julgado pelas funções DO ENVIO —
 * resolveDispatchPhone e resolveDispatchParams —, nunca por uma cópia. O
 * telefone primeiro; depois o nome e a placa, quando a regra os exige. null =
 * o disparo acharia tudo. A API (422) e o formulário (antes do envio) chamam
 * esta MESMA função.
 */
export function manualTicketSheetProblem(
  requirement: DispatchSheetRequirement | null,
  ticket: DispatchSheetTicket,
  check: { phone: boolean; nameAndPlate: boolean } = { phone: true, nameAndPlate: true }
): DispatchSheetErrorCode | null {
  if (!requirement) return null;
  if (check.phone && requirement.phone) {
    const phone = resolveDispatchPhone({
      description: ticket.description,
      requestorPhone: ticket.requestorPhone,
      createdVia: ticket.createdVia,
      sheet: ticket.sheet,
    });
    if (phone === null) return 'dispatch_phone_required';
  }
  if (check.nameAndPlate) {
    if (requirement.name && !resolveDispatchParams([{ source: 'first_name' }], ticket).ok) {
      return 'dispatch_name_required';
    }
    if (requirement.plate && !resolveDispatchParams([{ source: 'plate' }], ticket).ok) {
      return 'dispatch_plate_required';
    }
  }
  return null;
}

/** Lê `phoneSources` de um payload de evento (array das fontes conhecidas) — undefined se não for. */
export function dispatchPhoneSourcesOf(value: unknown): DispatchPhoneSource[] | undefined {
  const parsed = z.array(DispatchPhoneSourceSchema).min(1).safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Onde UMA tentativa que falhou com `phone_not_found` procurou o telefone, pelo
 * que o evento DELA gravou (`phoneSources`, desde o erp#43 de 03/10):
 * - gravado e válido: ele;
 * - AUSENTE (undefined/null): o evento é anterior ao #43, quando o disparo só
 *   lia a descrição — só a descrição, seja qual for a origem do ticket;
 * - presente mas torto: vazio — não se sabe, e o rótulo fica o genérico, que
 *   não afirma lugar nenhum.
 *
 * Nunca a regra de hoje (dispatchPhoneSources): o #7520 falhou em 02/10 lendo
 * só a descrição, e o cartão, calculado com a regra de 03/10, dizia "nem no
 * solicitante" — a PoolTrack leu e perguntou se o número estava errado.
 */
export function failedAttemptPhoneSources(recorded: unknown): DispatchPhoneSource[] {
  if (recorded === undefined || recorded === null) return ['description'];
  return dispatchPhoneSourcesOf(recorded) ?? [];
}

// ── Parâmetros do modelo ────────────────────────────────────────────────────

export const DISPATCH_PARAM_SOURCES = [
  'first_name',
  'client_name',
  'plate',
  'ticket_number',
  'fixed',
] as const;
export const DispatchParamSourceSchema = z.enum(DISPATCH_PARAM_SOURCES);
export type DispatchParamSource = z.infer<typeof DispatchParamSourceSchema>;

/**
 * Rótulo de cada fonte de parâmetro. Nome do contato e identificador do
 * recurso vêm do perfil do tenant (spec 017): "Primeiro nome do associado" e
 * "Placa" no rastreamento veicular, "…do contato" e "Identificador" no
 * genérico. O artigo é masculino — é o dos substantivos dos presets.
 */
export function dispatchParamSourceLabel(
  source: DispatchParamSource,
  terms: Pick<Terms, 'contact' | 'resourceId'>
): string {
  switch (source) {
    case 'first_name':
      return `Primeiro nome do ${terms.contact}`;
    case 'client_name':
      return 'Nome do cliente (empresa)';
    case 'plate':
      return capitalize(terms.resourceId);
    case 'ticket_number':
      return 'Número do ticket';
    case 'fixed':
      return 'Texto fixo';
  }
}

export const DispatchParamSchema = z
  .object({
    source: DispatchParamSourceSchema,
    /** só para `fixed` */
    value: z.string().trim().max(200).nullish(),
  })
  .refine((p) => p.source !== 'fixed' || (p.value ?? '') !== '', {
    message: 'texto fixo vazio',
    path: ['value'],
  });
export type DispatchParam = z.infer<typeof DispatchParamSchema>;

/**
 * Primeiro nome com só a inicial maiúscula: "JOSÉ DA SILVA" → "José". A
 * mensagem é "Olá, José", não "Olá, JOSÉ DA SILVA" — e o título do SGR chega
 * todo em caixa alta.
 */
export function firstNameOf(name: string | null | undefined): string | null {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  if (first === '') return null;
  const lower = first.toLocaleLowerCase('pt-BR');
  return lower.charAt(0).toLocaleUpperCase('pt-BR') + lower.slice(1);
}

/**
 * O ticket como o disparo o lê para nome e placa. Origem, solicitante e recurso
 * são OBRIGATÓRIOS de propósito: quem resolve os parâmetros tem de dizer o que
 * o ticket tem — um consumidor que os esquecesse discordaria do envio calado.
 */
export interface DispatchParamTicket {
  ticketNumber: number;
  title: string;
  description: string | null;
  clientName: string | null;
  /** `tickets.created_via` — decide se o formulário entra (dispatchIdentitySources) */
  createdVia: string | null | undefined;
  /** o "Solicitante" (nome) do formulário, como foi digitado */
  requestorName: string | null | undefined;
  /** o nome do recurso ligado ao ticket (`equipments.name`, o "Recurso" do formulário) */
  equipmentName: string | null | undefined;
  /** o nome e a placa da ficha que uma pessoa escreveu (spec 031) — EMPTY_TICKET_SHEET sem ficha */
  sheet: Pick<TicketSheetSlots, 'name' | 'plate'>;
}

/** De onde o disparo tira o nome da pessoa do ticket (`sheet`: a ficha editada, spec 031). */
export type DispatchNameSource = 'sheet' | 'title' | 'description' | 'requestor';
/** De onde o disparo tira a placa (o identificador do recurso). */
export type DispatchPlateSource = 'sheet' | 'title' | 'description' | 'equipment';

/**
 * ONDE o disparo procura nome e placa deste ticket, NA ORDEM. Título e ficha da
 * descrição valem para todo ticket e vêm sempre antes; o formulário (o
 * solicitante para o nome, o recurso ligado para a placa) só entra no ticket
 * aberto à mão no ERP — a MESMA lista de origens do telefone
 * (DISPATCH_REQUESTOR_PHONE_ORIGINS, decisão do operador de 03/10 estendida a
 * nome e placa em 05/10). No importado do TiFlux o solicitante é o placeholder
 * de um terceiro (F-79) e o recurso não vem do espelho.
 */
export function dispatchIdentitySources(createdVia: string | null | undefined): {
  name: readonly DispatchNameSource[];
  plate: readonly DispatchPlateSource[];
} {
  // spec 031: a ficha editada vence; a placa da descrição vem ANTES da do
  // título (a ordem da busca e da OS)
  return ticketSheetFormLayer(createdVia)
    ? {
        name: ['sheet', 'title', 'description', 'requestor'],
        plate: ['sheet', 'description', 'title', 'equipment'],
      }
    : { name: ['sheet', 'title', 'description'], plate: ['sheet', 'description', 'title'] };
}

const cleanText = (v: string | null | undefined): string | null => {
  const t = v?.trim() ?? '';
  return t === '' ? null : t;
};

/** placa como vai na mensagem: sem hífen, maiúscula — a de sempre do título e da ficha */
const plateText = (v: string | null | undefined): string | null =>
  cleanText(v?.replace(/-/g, '').toUpperCase());

export interface ResolvedDispatchIdentity {
  /** o nome INTEIRO da pessoa (o parâmetro usa o primeiro, firstNameOf) */
  name: { value: string; source: DispatchNameSource } | null;
  /** a placa já normalizada (sem hífen, maiúscula) */
  plate: { value: string; source: DispatchPlateSource } | null;
}

/** a origem da ficha → a do disparo (a pessoa é `sheet`) */
const identitySource = <T extends string>(source: string | null): T =>
  (source === 'person' ? 'sheet' : source) as T;

/**
 * O nome e a placa do DISPARO, e de onde cada um veio — a ÚNICA decisão deles
 * (o envio, a lista "Disparos", as variáveis da resposta rápida e a trava do
 * ticket aberto à mão chamam esta função). São CAMADAS da ficha do ticket
 * (spec 031, resolveSheetSlot):
 *   - nome: a ficha editada > título `PLACA-CIDADE-NOME` > `NOME:` da descrição
 *     > SÓ no `web`, o solicitante do formulário;
 *   - placa: a ficha editada > `PLACA:` da descrição > título > SÓ no `web`, o
 *     recurso ligado com cara de identificador (PLATE_LIKE, em ticket-sheet).
 * O que a pessoa apagou (`''`) não tem substituto: o disparo fica sem o valor,
 * como ela quis. O formulário nunca passa à frente da ficha nem do título.
 */
export function resolveDispatchIdentity(ticket: DispatchParamTicket): ResolvedDispatchIdentity {
  const input: TicketSheetSourceInput = {
    title: ticket.title,
    description: ticket.description,
    createdVia: ticket.createdVia,
    requestorName: ticket.requestorName ?? null,
    requestorPhone: null,
    equipmentName: ticket.equipmentName ?? null,
    sgrVehicleId: null,
    sheet: { ...EMPTY_TICKET_SHEET, name: ticket.sheet.name, plate: ticket.sheet.plate },
  };
  const nameSlot = resolveSheetSlot('name', input);
  const plateSlot = resolveSheetSlot('plate', input);
  const nameValue = cleanText(nameSlot.value);
  const plateValue = plateText(plateSlot.value);
  return {
    name:
      nameValue !== null
        ? { value: nameValue, source: identitySource<DispatchNameSource>(nameSlot.source) }
        : null,
    plate:
      plateValue !== null
        ? { value: plateValue, source: identitySource<DispatchPlateSource>(plateSlot.source) }
        : null,
  };
}

export type ResolvedDispatchParams =
  { ok: true; values: string[] } | { ok: false; missing: DispatchParamSource };

/**
 * Valores posicionais do modelo ({{1}}, {{2}}, …) a partir do ticket.
 *
 * Nome e placa saem de resolveDispatchIdentity: o título `PLACA-CIDADE-NOME`
 * (parseSgrTitle, spec 016) — o mesmo formato que o worker exigia —, na falta
 * dele a ficha do SGR na descrição (`PLACA:`, `NOME:`) e, só no ticket aberto
 * à mão no ERP, o formulário (solicitante e recurso). Qualquer fonte vazia
 * torna o disparo inválido: modelo com "Olá, " e o nome em branco é pior que
 * não mandar, e o ticket vai para "Dados inválidos" para alguém completar. O
 * motivo continua `missing_param:<fonte>`.
 */
export function resolveDispatchParams(
  params: readonly DispatchParam[],
  ticket: DispatchParamTicket
): ResolvedDispatchParams {
  let identity: ResolvedDispatchIdentity | null = null;
  const identityOf = () => (identity ??= resolveDispatchIdentity(ticket));

  const values: string[] = [];
  for (const param of params) {
    let value: string | null | undefined;
    switch (param.source) {
      case 'first_name':
        value = firstNameOf(identityOf().name?.value);
        break;
      case 'client_name':
        value = ticket.clientName;
        break;
      case 'plate':
        value = identityOf().plate?.value;
        break;
      case 'ticket_number':
        value = String(ticket.ticketNumber);
        break;
      case 'fixed':
        value = param.value;
        break;
    }
    const clean = value?.trim() ?? '';
    if (clean === '') return { ok: false, missing: param.source };
    values.push(clean);
  }
  return { ok: true, values };
}

// ── Status e motivos ────────────────────────────────────────────────────────

export const TICKET_DISPATCH_STATUSES = [
  'pending',
  'sending',
  'sent',
  'delivered',
  'failed',
  'skipped',
  'replied',
] as const;
export const TicketDispatchStatusSchema = z.enum(TICKET_DISPATCH_STATUSES);
export type TicketDispatchStatus = z.infer<typeof TicketDispatchStatusSchema>;

export const TICKET_DISPATCH_STATUS_LABELS: Record<TicketDispatchStatus, string> = {
  pending: 'Na fila de disparo',
  sending: 'Disparando',
  sent: 'Disparo realizado',
  delivered: 'Disparo entregue',
  failed: 'Disparo falhou',
  skipped: 'Fora da fila',
  replied: 'Cliente respondeu',
};

/**
 * Status que OCUPAM vaga na cota do dia em que foram reivindicados. `failed`
 * e `skipped` devolvem a vaga — nada chegou ao cliente (a regra do worker:
 * falha refunda). `replied` continua contando: o disparo saiu e foi lido.
 */
export const DISPATCH_CAP_STATUSES: readonly TicketDispatchStatus[] = [
  'sending',
  'sent',
  'delivered',
  'replied',
];

/**
 * Motivos gravados em `last_error` (prefixo antes do ':' quando houver
 * detalhe). O painel agrupa por eles e a tela do ticket os traduz.
 */
export const DISPATCH_REASON_LABELS: Record<string, string> = {
  'stale:closed': 'Ticket fechado antes da vez',
  'stale:wrong_stage': 'Ticket saiu do estágio de entrada',
  'stale:wrong_desk': 'Ticket mudou de mesa',
  'stale:unknown_ticket': 'Ticket não encontrado',
  'stale:desk_mirrored': 'Mesa voltou a ser espelhada do TiFlux',
  // spec 025, D6: em mesa espelhada, a regra só alcança cliente operado no ERP
  'stale:client_not_operated': 'Cliente fora da lista dos operados no ERP',
  open_chat: 'Contato já em conversa aberta',
  removed_manually: 'Retirado da fila à mão',
  blocked_client: 'Cliente bloqueado na regra',
  phone_not_found: 'Sem telefone válido',
  // missing_param:first_name e :plate dependem do vocabulário do tenant — ver
  // dispatchReasonLabel
  'missing_param:client_name': 'Sem nome do cliente',
  'missing_param:ticket_number': 'Sem número do ticket',
  'missing_param:fixed': 'Texto fixo vazio',
  meta_failed: 'A Meta recusou a mensagem',
  broker_denied: 'Número na lista de bloqueio do WhatsApp',
  broker_rejected: 'Mensagem rejeitada pelo provedor',
  // spec 025, fase 3: o broker recusou a marca na Blip — o destinatário não tem
  // identidade lá (BSUID). É o dado do contato, como a Meta recusar
  'broker_rejected:blip_unsupported': 'A Blip não reconhece o contato',
  send_failed: 'Falha de envio após tentativas',
  stuck_sending: 'Envio interrompido (reiniciado)',
  send_error: 'Erro interno ao disparar',
  // spec 025, fase 3 — o disparo VOLTA para a fila (nada chegou ao contato, e
  // não é dado inválido). O ticket volta ao estágio de entrada e sai de novo
  // num ciclo seguinte; o painel mostra quantos estão assim
  'postponed:contact_busy_in_other_service':
    'Adiado: o contato está em atendimento em outro serviço da Blip',
  'postponed:blip_disabled': 'Adiado: a Blip está desligada no broker',
  'postponed:blip_dispatch_stopped': 'Adiado: o disparo pela Blip foi parado pelo interruptor',
  postponed: 'Adiado',
  // mais adiamentos que o teto: sai da fila SEM ir para "Dados inválidos" — o
  // atendente decide ("Disparar agora" reaproveita a linha)
  postponed_too_long: 'Adiado vezes demais — dispare à mão quando o contato estiver livre',
  // o TiFlux, na última volta do ciclo dos operados, NÃO tinha o ticket no
  // estágio de entrada: o worker pode já ter mandado o modelo (D7 da 025)
  'waiting:tiflux_stage': 'Aguardando o TiFlux: lá o ticket não está no estágio de entrada',
};

/** O que o último ciclo concluiu (`last_cycle_note` da regra). */
export const DISPATCH_CYCLE_NOTE_LABELS: Record<string, string> = {
  ok: 'Disparou',
  queue_empty: 'Fila vazia',
  disabled: 'Regra desligada — a fila é alimentada e podada, sem disparo',
  channel_missing: 'Canal de WhatsApp removido',
  channel_inactive: 'Canal de WhatsApp inativo',
  integration_inactive: 'Integração WhatsApp inativa',
  outside_window: 'Fora do horário',
  wrong_day: 'Dia sem disparo',
  cap_reached: 'Cota do dia atingida',
  // spec 025, fase 3
  channel_not_blip: 'Mesa espelhada do TiFlux: o disparo só sai por um número da Blip',
  blip_dispatch_stopped: 'Disparo pela Blip parado pelo interruptor',
};

/**
 * O motivo em português. `phoneSources` é onde A TENTATIVA procurou o telefone
 * (o que o evento dela gravou — failedAttemptPhoneSources; a API o entrega no
 * selo e na lista): com ele, `phone_not_found` diz onde procurou; sem ele (o
 * painel da mesa, que agrupa tickets de toda origem, ou falha sem evento),
 * fica o rótulo genérico, que não afirma lugar nenhum.
 */
export function dispatchReasonLabel(
  reason: string | null | undefined,
  terms: Pick<Terms, 'contact' | 'resourceId'>,
  phoneSources?: readonly DispatchPhoneSource[] | null
): string | null {
  if (!reason) return null;
  if (reason === 'phone_not_found' && phoneSources && phoneSources.length > 0) {
    return phoneNotFoundLabel(phoneSources);
  }
  if (reason === 'missing_param:first_name') return `Sem nome do ${terms.contact} no título`;
  if (reason === 'missing_param:plate') return `Sem ${terms.resourceId} no título`;
  return DISPATCH_REASON_LABELS[reason] ?? DISPATCH_REASON_LABELS[reason.split(':')[0]!] ?? reason;
}

/**
 * O que um evento do disparo (dispatch_sent/failed/replied) diz no histórico
 * além da troca de estágio: o motivo — `phone_not_found` com ONDE AQUELA
 * tentativa procurou (`phoneSources` do payload; o evento anterior a 03/10 não
 * as traz e procurou só na descrição — failedAttemptPhoneSources) —, no
 * enviado, de onde veio o número (`phoneSource`: "telefone do solicitante") e,
 * quando o 1º contato não foi lançado nos apontamentos, por quê. O número nunca
 * está no evento.
 */
export function dispatchEventNotes(
  payload: Record<string, unknown>,
  terms: Pick<Terms, 'contact' | 'resourceId'>
): string[] {
  const notes: string[] = [];
  const code = typeof payload['reason'] === 'string' ? payload['reason'] : null;
  const reason = dispatchReasonLabel(
    code,
    terms,
    code === 'phone_not_found' ? failedAttemptPhoneSources(payload['phoneSources']) : undefined
  );
  if (reason) notes.push(reason);
  const source = DispatchPhoneSourceSchema.safeParse(payload['phoneSource']);
  if (source.success) notes.push(DISPATCH_PHONE_SOURCE_LABELS[source.data]);
  if (payload['firstContact'] === 'no_author') notes.push(FIRST_CONTACT_NO_AUTHOR_NOTE);
  if (payload['firstContact'] === 'skipped_inactive_target') notes.push(FIRST_CONTACT_INACTIVE_NOTE);
  return notes;
}

// ── 1º contato no apontamento (pedido da PoolTrack, 05/10) ──────────────────

/**
 * Duração do apontamento do 1º contato: ZERO — início = fim = o envio
 * (decisão do operador, 05/10). É registro, não trabalho: não soma hora no
 * atendente, na folha, no extrato/PDF nem no consumo do contrato por horas. O
 * domínio aceita o zero só com a origem do 1º contato
 * (appointmentPeriodError), e a edição que não mexe no período não o rejulga
 * (appointmentEditPeriodError) — a descrição se corrige.
 */
export const FIRST_CONTACT_APPOINTMENT_MINUTES = 0;

/**
 * A descrição do apontamento do 1º contato. SEM telefone (o número fica no
 * disparo); o termo do contato é o do tenant ("associado" na PoolTrack).
 */
export function firstContactAppointmentText(input: {
  contactTerm: string;
  templateName: string;
  manual: boolean;
}): string {
  return `1º contato com o ${input.contactTerm}: disparo pelo WhatsApp (${input.templateName}), ${
    input.manual ? 'manual' : 'automático'
  }`;
}

/** o que o histórico diz quando o envio não lançou o 1º contato (sem autor) */
export const FIRST_CONTACT_NO_AUTHOR_NOTE =
  '1º contato não lançado nos apontamentos: o ticket não tem responsável';
/** F-178: o ciclo não lança apontamento novo em nome de quem a empresa desligou */
export const FIRST_CONTACT_INACTIVE_NOTE =
  '1º contato não lançado nos apontamentos: o responsável está desligado nesta empresa';

// ── Linha do tempo e ações do disparo (o ticket e a lista "Disparos") ───────

type DispatchTimes = Pick<
  TicketDispatch,
  | 'status'
  | 'enqueuedAt'
  | 'sentAt'
  | 'deliveredAt'
  | 'failedAt'
  | 'repliedAt'
  | 'lastError'
  | 'postponedUntil'
>;

/**
 * O motivo que vale AGORA: o de quem falhou, saiu da fila ou voltou para ela
 * (adiado pela Blip, esperando o TiFlux). Nos outros status o `last_error` é
 * resto de um adiamento anterior — o envio não o apaga — e mostrá-lo diria que
 * um disparo entregue "falhou".
 */
export function dispatchReasonOf(d: Pick<DispatchTimes, 'status' | 'lastError'>): string | null {
  return d.status === 'failed' || d.status === 'skipped' || d.status === 'pending'
    ? d.lastError
    : null;
}

export type DispatchStepKind =
  | 'enqueued'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'replied'
  | 'failed'
  | 'skipped'
  | 'postponed'
  | 'waiting';

export const DISPATCH_STEP_LABELS: Record<DispatchStepKind, string> = {
  enqueued: 'Na fila',
  sending: 'Disparando',
  sent: 'Disparado',
  delivered: 'Entregue',
  replied: 'Respondeu',
  failed: 'Falhou',
  skipped: 'Fora da fila',
  postponed: 'Adiado',
  waiting: 'Esperando',
};

export interface DispatchStep {
  kind: DispatchStepKind;
  /** quando aconteceu; no adiado, ATÉ quando espera (null = sem hora) */
  at: Date | string | null;
  /** código do motivo (DISPATCH_REASON_LABELS) — falhou, fora da fila, adiado */
  reason: string | null;
}

/**
 * Os passos que ACONTECERAM, na ordem: Na fila → Disparado → Entregue →
 * Respondeu, ou o desfecho que interrompeu (Falhou, Fora da fila, Adiado). Sai
 * dos carimbos da linha, não do status: um disparo respondido sem o aviso de
 * entrega (antes de 01/10 a Blip não mandava) não ganha um "Entregue" que
 * ninguém viu, e um que falhou depois de sair mostra que saiu.
 */
export function dispatchSteps(d: DispatchTimes): DispatchStep[] {
  const steps: DispatchStep[] = [{ kind: 'enqueued', at: d.enqueuedAt, reason: null }];
  if (d.status === 'sending') steps.push({ kind: 'sending', at: null, reason: null });
  if (d.sentAt) steps.push({ kind: 'sent', at: d.sentAt, reason: null });
  if (d.deliveredAt) steps.push({ kind: 'delivered', at: d.deliveredAt, reason: null });
  if (d.repliedAt) steps.push({ kind: 'replied', at: d.repliedAt, reason: null });
  const reason = dispatchReasonOf(d);
  if (d.status === 'failed') steps.push({ kind: 'failed', at: d.failedAt, reason });
  if (d.status === 'skipped') steps.push({ kind: 'skipped', at: d.failedAt, reason });
  // de volta à fila (spec 025, fase 3): adiado pela Blip ou esperando o TiFlux
  if (d.status === 'pending' && reason) {
    steps.push({
      kind: reason.startsWith('postponed') ? 'postponed' : 'waiting',
      at: d.postponedUntil,
      reason,
    });
  }
  return steps;
}

export interface DispatchActions {
  canDispatch: boolean;
  canRemove: boolean;
}

const NO_DISPATCH_ACTIONS: DispatchActions = { canDispatch: false, canRemove: false };

/**
 * O que o atendente pode fazer com o disparo deste ticket agora — "Disparar
 * agora" e "Tirar da fila de disparo". UMA regra para a visão completa e a
 * compacta: só com a regra da mesa ligada para este ticket (`ruleEnabled`, que
 * a API já nega a ticket espelhado ou fora do alcance — spec 025, D6), com o
 * ticket aberto e fora do espelho. Reenvia à mão o que não chegou ao cliente;
 * só tira da fila o que está nela.
 */
export function dispatchActionsFor(input: {
  dispatch: Pick<TicketDispatch, 'status'> | null;
  ruleEnabled: boolean;
  ticketClosed: boolean;
  ticketMirrored: boolean;
}): DispatchActions {
  if (!input.ruleEnabled || input.ticketClosed || input.ticketMirrored) return NO_DISPATCH_ACTIONS;
  const status = input.dispatch?.status ?? null;
  return {
    canDispatch:
      status === null || status === 'pending' || status === 'skipped' || status === 'failed',
    canRemove: status === 'pending',
  };
}

// ── Período da lista "Disparos" ─────────────────────────────────────────────

/** O padrão da lista: hoje e os 7 dias anteriores (em Brasília). */
export const DISPATCH_LIST_DEFAULT_DAYS = 7;
/** Teto do período pedido — um trimestre; mais que isso é relatório, não lista. */
export const DISPATCH_LIST_MAX_DAYS = 92;

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` que existe no calendário (2026-02-30 não existe). */
export function isValidDateKey(key: string): boolean {
  const m = DATE_KEY_RE.exec(key);
  if (!m) return false;
  const t = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return t.toISOString().slice(0, 10) === key;
}

/** `YYYY-MM-DD` deslocado `days` dias civis — aritmética de calendário, sem fuso. */
export function shiftDateKey(key: string, days: number): string {
  const m = DATE_KEY_RE.exec(key);
  if (!m) throw new Error(`data fora do formato YYYY-MM-DD: ${key}`);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days))
    .toISOString()
    .slice(0, 10);
}

/** Dias civis de `from` a `to`, contando os dois (o mesmo dia = 1). */
export function dateKeySpanDays(from: string, to: string): number {
  const day = (k: string) => Date.parse(`${k}T00:00:00Z`) / 86_400_000;
  return day(to) - day(from) + 1;
}

/**
 * O período da lista quando a pessoa não escolheu: hoje e os 7 dias anteriores
 * em Brasília. Só uma ponta escolhida completa a outra: sem `to`, até hoje; sem
 * `from`, os 7 dias antes do `to`.
 */
export function resolveDispatchListRange(
  input: { from?: string | undefined; to?: string | undefined },
  now: Date = new Date()
): { from: string; to: string } {
  const to = input.to ?? dateKeyInBRT(now);
  return { from: input.from ?? shiftDateKey(to, -DISPATCH_LIST_DEFAULT_DAYS), to };
}

// ── Contratos HTTP ──────────────────────────────────────────────────────────

const HHMM = z.string().regex(HHMM_RE, 'HH:MM');

/** GET /desks/:id/dispatch-rule — `null` quando a mesa ainda não tem regra. */
export const TicketDispatchRuleSchema = z.object({
  id: z.number().int(),
  deskId: z.number().int(),
  channelId: z.number().int(),
  templateName: z.string(),
  templateLanguage: z.string(),
  /** corpo do modelo como estava ao salvar — o texto que vai para o histórico */
  templateBody: z.string(),
  params: z.array(DispatchParamSchema),
  entryStageId: z.number().int(),
  sentStageId: z.number().int(),
  invalidStageId: z.number().int(),
  repliedStageId: z.number().int(),
  dailyCap: z.number().int(),
  weekdays: z.array(z.number().int()),
  windowStart: z.string(),
  windowEnd: z.string(),
  blockedClientIds: z.array(z.number().int()),
  enabled: z.boolean(),
  /**
   * Spec 031 (D5): com a regra ligada, o ticket ABERTO À MÃO nesta mesa precisa
   * do telefone (e do nome e da placa que o modelo usa) para ser criado. É o
   * interruptor do lado rápido: desligá-lo não exige deploy nem desliga o
   * disparo — o ticket sem telefone volta a cair em "Dados inválidos" no ciclo.
   */
  requireSheetOnManual: z.boolean(),
  /** último ciclo e o que ele concluiu (ok, fora da janela, canal inativo…) */
  lastCycleAt: Dateish.nullable(),
  lastCycleNote: z.string().nullable(),
  updatedAt: Dateish,
});
export type TicketDispatchRule = z.infer<typeof TicketDispatchRuleSchema>;

export const TicketDispatchRulePutBodySchema = z
  .object({
    channelId: z.number().int().positive(),
    templateName: z.string().trim().min(1).max(512),
    templateLanguage: z.string().trim().min(1).max(20),
    params: z.array(DispatchParamSchema).max(20),
    entryStageId: z.number().int().positive(),
    sentStageId: z.number().int().positive(),
    invalidStageId: z.number().int().positive(),
    repliedStageId: z.number().int().positive(),
    dailyCap: z.number().int().min(1).max(DISPATCH_MAX_DAILY_CAP),
    weekdays: z
      .array(z.number().int().min(0).max(6))
      .min(1)
      .transform((days) => [...new Set(days)].sort((a, b) => a - b)),
    windowStart: HHMM,
    windowEnd: HHMM,
    blockedClientIds: z
      .array(z.number().int().positive())
      .max(500)
      .transform((ids) => [...new Set(ids)]),
    enabled: z.boolean(),
    /**
     * "Exigir a ficha ao abrir à mão" (spec 031). Ausente = mantém o que a regra
     * tem (na regra nova, ligado — o padrão da coluna).
     */
    requireSheetOnManual: z.boolean().optional(),
    /**
     * Texto do modelo como aprovado na Meta, SÓ para canal da Blip (spec 025,
     * fase 3): os modelos do número da Blip vivem na conta da Blip, e a lista da
     * Zeepchat não os mostra. Vale quando o modelo não está na lista; é o texto
     * que vai para o histórico da conversa (o que sai é o nome + os parâmetros).
     * Em canal da Meta é ignorado — lá a lista manda.
     */
    templateBody: z.string().trim().min(1).max(BLIP_TEMPLATE_BODY_MAX).optional(),
  })
  .refine((b) => (parseHHMM(b.windowStart) ?? 0) < (parseHHMM(b.windowEnd) ?? 0), {
    message: 'a janela termina antes de começar',
    path: ['windowEnd'],
  })
  .refine(
    // entrar e sair pelo mesmo estágio reenfileiraria o que acabou de sair
    (b) =>
      b.entryStageId !== b.sentStageId &&
      b.entryStageId !== b.invalidStageId &&
      b.entryStageId !== b.repliedStageId,
    { message: 'o estágio de entrada não pode ser também de saída', path: ['entryStageId'] }
  );
export type TicketDispatchRulePutBody = z.infer<typeof TicketDispatchRulePutBodySchema>;

/** GET /tickets/:n/dispatch — o selo do ticket (`null` = nunca entrou na fila). */
export const TicketDispatchSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  status: TicketDispatchStatusSchema,
  phone: z.string().nullable(),
  chatId: z.number().int().nullable(),
  manual: z.boolean(),
  enqueuedAt: Dateish,
  sentAt: Dateish.nullable(),
  deliveredAt: Dateish.nullable(),
  failedAt: Dateish.nullable(),
  repliedAt: Dateish.nullable(),
  lastError: z.string().nullable(),
  /** spec 025, fase 3: o adiado só volta a sair a partir daqui (null = sem espera) */
  postponedUntil: Dateish.nullable(),
  /** quantas vezes a Blip adiou este disparo (o "Disparar agora" zera) */
  postponeCount: z.number().int(),
});
export type TicketDispatch = z.infer<typeof TicketDispatchSchema>;

export const TicketDispatchResponseSchema = z.object({
  dispatch: TicketDispatchSchema.nullable(),
  /** a mesa do ticket tem regra ligada — mostra as ações manuais */
  ruleEnabled: z.boolean(),
  /**
   * O número da empresa do disparo: o da conversa que o envio criou (por onde
   * SAIU) ou, antes de sair, o da regra da mesa (por onde VAI sair). null =
   * nem disparo nem regra.
   */
  channel: ChatChannelSchema.nullable(),
  /**
   * O que a tela diz quando o disparo falha sem telefone:
   * - disparo `failed` com `phone_not_found`: onde AQUELA tentativa procurou —
   *   o que o evento dela gravou (failedAttemptPhoneSources: evento anterior a
   *   03/10 = só a descrição); vazio quando a falha não deixou evento (a tela
   *   cai no rótulo genérico, que não afirma lugar);
   * - nos outros: onde o PRÓXIMO disparo procura, na ordem
   *   (dispatchPhoneSources) — a regra de hoje, que vale para o que ainda vai
   *   sair.
   */
  phoneSources: z.array(DispatchPhoneSourceSchema),
});
export type TicketDispatchResponse = z.infer<typeof TicketDispatchResponseSchema>;

const DispatchDateKey = z
  .string()
  .regex(DATE_KEY_RE, 'YYYY-MM-DD')
  .refine(isValidDateKey, { message: 'data inexistente' });

/**
 * GET /tickets/dispatches — a lista "Disparos" (total no X-Total-Items). O
 * período é o do MOVIMENTO mais recente de cada disparo, em Brasília; sem
 * período, hoje e os 7 dias anteriores (resolveDispatchListRange).
 */
export const DispatchListQuerySchema = PaginationQuerySchema.extend({
  /** mesa da regra que disparou */
  desk: IdCoerce.optional(),
  status: TicketDispatchStatusSchema.optional(),
  from: DispatchDateKey.optional(),
  to: DispatchDateKey.optional(),
});
export type DispatchListQuery = z.infer<typeof DispatchListQuerySchema>;

/**
 * Uma linha da lista. SEM telefone, de propósito: a lista é da equipe e o
 * ticket já o tem — o que não sai do banco não vaza na tela, no log nem no
 * print de alguém.
 */
export const DispatchListItemSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  ticketTitle: z.string(),
  /** a pessoa do ticket (título `PLACA-CIDADE-NOME` ou a ficha); null = o ticket não diz */
  contactName: z.string().nullable(),
  clientName: z.string().nullable(),
  /** a mesa da regra que disparou */
  desk: z.object({ id: z.number().int(), name: z.string() }),
  status: TicketDispatchStatusSchema,
  manual: z.boolean(),
  enqueuedAt: Dateish,
  sentAt: Dateish.nullable(),
  deliveredAt: Dateish.nullable(),
  failedAt: Dateish.nullable(),
  repliedAt: Dateish.nullable(),
  lastError: z.string().nullable(),
  postponedUntil: Dateish.nullable(),
  /** o movimento mais recente — a ordem da lista e o que o período filtra */
  lastActivityAt: Dateish,
  /**
   * onde o disparo procurou (falha `phone_not_found`: o que o evento dela
   * gravou) ou procura (os outros) o telefone — a mesma regra do
   * TicketDispatchResponse. Sem o número.
   */
  phoneSources: z.array(DispatchPhoneSourceSchema),
});
export type DispatchListItem = z.infer<typeof DispatchListItemSchema>;

/** GET /tickets/dispatches/desks — as mesas com regra de disparo que a pessoa enxerga */
export const DispatchDeskSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  enabled: z.boolean(),
});
export type DispatchDesk = z.infer<typeof DispatchDeskSchema>;

/** GET /desks/:id/dispatch-summary */
export const DispatchSummarySchema = z.object({
  /** data em Brasília a que "hoje" se refere */
  date: z.string(),
  sentToday: z.number().int(),
  dailyCap: z.number().int(),
  queued: z.number().int(),
  repliedToday: z.number().int(),
  /** falhas e ignorados de hoje, por motivo */
  failuresToday: z.array(
    z.object({ status: z.enum(['failed', 'skipped']), reason: z.string(), count: z.number().int() })
  ),
  /**
   * Na fila DE VOLTA (spec 025, fase 3): saíram e voltaram — adiados pela Blip
   * (contato em outro serviço, Blip desligada, interruptor) —, por motivo. Estão
   * dentro de `queued`.
   */
  requeued: z.array(z.object({ reason: z.string(), count: z.number().int() })),
  /**
   * Na fila e esperando o TiFlux (D7 da 025): o ciclo dos operados viu o ticket
   * fora do estágio de entrada lá — o worker pode já ter mandado o modelo. Não
   * saem até a pendência ser resolvida ("Puxar do TiFlux"). Estão em `queued`.
   */
  waitingTiflux: z.number().int(),
  /** contagem por status, total da mesa (bate com o banco) */
  byStatus: z.record(TicketDispatchStatusSchema, z.number().int()),
});
export type DispatchSummary = z.infer<typeof DispatchSummarySchema>;
